import React, { Suspense, lazy, useEffect, useState } from 'react'
import { useAppDispatch } from '../../store'
import { type ApplyFiltersAndDispatchParams, type Row } from '../../components/CustomTable/types'
import LoadingPage from '../../pages/LoadingPage'
import StudentsTopbarActions from './StudentsTopbarActions'
import { getStudentsApi } from '../../store/slices/studentsSlice/apis'
import { getLoading, getStudentsList } from '../../store/slices/studentsSlice/selectors'
import DeleteFacultyAndStudent from '../FacultyAndStaff/DeleteFaculty'
import CreateAndEditStudents from './CreateAndEditStudents'
import { exportCsv } from '../../utils/exportCsv'
import { notification } from '../../components/Notifications'
import UserManager from '../../APIManager/UserManager'
import { useMutation } from '@tanstack/react-query'

const AppTable = lazy(async () => await import('../../components/CustomTable'))

const Students = (): JSX.Element => {
  const isLoading = getLoading()
  const studentList = getStudentsList()
  const dispatch = useAppDispatch()

  const [params, setParams] = useState<ApplyFiltersAndDispatchParams>()

  const columns = studentList?.keys ?? []
  const rows: Row[] = studentList?.data ?? []
  const tableHeader = studentList?.header?.headerName ?? ''

  useEffect(() => {
    void dispatch(getStudentsApi({ params }))
  }, [params])

  const { mutate: exportStudentCSVMutate, isPending: exportLoading } = useMutation({
    mutationFn: UserManager.exportStudentsCSV,
    onSuccess: (response) => {
      const data = response?.data
      if (data) {
        exportCsv({ data, headers: response?.headers })
      }
    },
    onError: (error: any) => {
      notification(error?.message ?? 'Export csv failed', { variant: 'error' })
    }
  })

  const handleExport = (): void => {
    const exportParams = {
      search: params?.search || '',
      sort: params?.sort || undefined
    }
    exportStudentCSVMutate(exportParams)
  }

  return (
    <Suspense fallback={<LoadingPage />}>
      <AppTable
        topbarTitle={tableHeader}
        customTopbarAction={<StudentsTopbarActions />}
        exportCsv={handleExport}
        exportButtonProps={{ loading: exportLoading, disabled: exportLoading }}
        list={studentList}
        rows={rows}
        loading={isLoading}
        columns={columns}
        featureName="Student"
        editAction={<CreateAndEditStudents />}
        deleteAction={<DeleteFacultyAndStudent params={params} isStudent={true} />}
        actionModalEntityDisplayField='firstName'
        params={params}
        setParams={setParams}
      />
    </Suspense>
  )
}

export default Students
